import React from 'react';
import { humanize, titleize, unescapeHTML } from 'underscore.string';
import { has, sortBy as _sortBy } from 'lodash';
import isNil from 'lodash/isNil';
import formatMoney from 'utils/formatters/Money';
import { NO_DATA } from 'utils/constants';
import { phoneFormatter } from 'components/modules/customers/formatters';
import { firstLetterTitlization, sluggify } from 'utils/localization/utils';
import { OPS_GROUP_OPTIONS } from 'constants/shipper';
import { t } from 'i18next';
import { formatDate } from 'utils/formatters/Date';
import { getUniqueStatusList } from 'utils/ContainerTrackAPIUtils';
import {
  getContainerStatuses,
  getUniqueMarketCodes,
  getColumnBlacklist,
} from 'utils/UnifiedViewWebAPIUtils';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import CargomaticTooltip from 'components/v2/Tooltip';
import {
  billableEventList,
  billingStatusList,
  invoiceGroupingFilterList,
} from 'constants/billing-status';
import { format, toDate, yearsAgo } from 'helpers/dates';
import { isUS } from 'helpers/helper_functions';
import { getReferenceNumbersMasterList } from 'utils/UserServiceAPIUtils';
import usStates from 'constants/us-states';
import {
  OPENTRACK_TRACKING_STATUS,
  MASTER_BOL_STATUS,
} from 'constants/Opentrack';
import {
  DEFAULT_DRAYAGE_COLS_OPS,
  MISSING_ACCESSORIAL,
  SHIPMENT_PRE_ARRIVAL_STATUS_BADGE_COLOR,
  SHIPMENT_PRE_ARRIVAL_STATUS_FILTER_LIST,
  STANDARD_REFERENCE_NUMBERS,
  STOP_TYPES,
  TIME_SELECTIONS,
} from './constants';
import {
  onDateCellEdit,
  onAdminNotesEdit,
  onShipmentCommentAdd,
  onShipmentChangeTags,
  onUpdatePODStatus,
} from './updateFunctions.js';
import CustomerMultiSearchFilter from './customFilters/CustomerMultiSearchFilter';
import TextFilterStartsWith from './customFilters/TextFilterStartsWith';
import TextFilterStartsWithNotStarts from './customFilters/TextFilterStartsWithNotStarts';

import { getMinValidDate } from './transformers';

import {
  tagFormatter,
  statusFormatter,
  portIntegrationsFormatter,
  marginFormatter,
  deliveryOrderFormatter,
  checkedFormatter,
  lineSeparatedStringFormatter,
  orderIDRenderer,
  opentrackTrackingStatusFormatter,
  shipmentIDRenderer,
  shipperShipmentIDRenderer,
  masterBolStatusFormatter,
  weightRenderer,
  distanceFormatter,
  legTypeList,
  legTypeRenderer,
} from './formatters';
import { shipmentStatuses } from '../shipments/mappings';
import CommentsEditor from './customCellEditors/CommentsEditor';
import TagsEditor from './customCellEditors/TagsEditor';
import { HoverBadges } from './customCellRenderers/HoverBadges';
import { showToast } from 'stores/ToastStore';
import { getDocumentTypes } from 'utils/DocumentsAPIUtils';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import { overrideBillingStatusReasonCodes } from 'constants/override-billing-status';
import Badge from 'components/v2/Badge';
import BillingStatusBadge from 'components/v2/BillingStatusBadge';
import { BillingStatusPendingReviewEditor } from './customCellEditors/BillingStatusPendingReviewEditor';
import { TerminalHoldStatusEditor } from 'components/modules/master/customCellEditors/TerminalHoldStatusEditor';
import EditButton from 'components/v2/buttons/EditButton';
import SessionStore from 'stores/SessionStore';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { getBridgeCodeFilters } from 'utils/BridgeCodeAPIUtils';
import ShipmentLegs from './customCellRenderers/ShipmentLegs';
import { ShipmentLegEditor } from './customCellEditors/ShipmentLegEditor';
import { TERMINAL_HOLD_STATUS_VALUES } from 'constants/terminalHoldStatus';
import { PRE_NOTICE_ACCESSORIAL_TYPES } from 'constants/preNoticeAccessorialTypes';

export const currencyFormatter = (params, { currency } = {}) => {
  if (!params) {
    return '';
  }
  return formatMoney(params.value, currency);
};

export const percentFormatter = (params) => {
  if (!params || isNil(params.value)) {
    return NO_DATA;
  }
  const num = Number(params.value);
  if (!Number.isFinite(num)) {
    return NO_DATA;
  }
  const pct = num * 100;
  return `${pct.toFixed(2)}%`;
};

const STOP_DELIMITER = '__';
const LATEST_STOP_PREFIX = `latest_completed${STOP_DELIMITER}`;
export function getNumberFilter() {
  return {
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      maxNumConditions: 10,
      filterOptions: [
        'equals',
        'notEqual',
        'greaterThan',
        'greaterThanOrEqual',
        'lessThan',
        'lessThanOrEqual',
        'inRange',
      ],
    },
  };
}

export const missingReferenceNumbersFilterParams = {
  buttons: ['reset', 'apply'],
  valueFormatter: (params) =>
    params.value ? titleize(humanize(t(sluggify(params.value)))) : params.value,

  values: async (params) => {
    const referenceNumbers = await fetchReferenceNumbersMasterList();
    params.success(referenceNumbers);
  },
};

export const documentsFilterParams = {
  buttons: ['reset', 'apply'],
  valueFormatter: (params) =>
    params.value ? titleize(humanize(t(sluggify(params.value)))) : params.value,

  values: async (params) => {
    const data = await getAvailableDocumentTypes('shipment');

    params.success((data || []).map((docType) => docType.toLowerCase()));
  },
};

export const missingAccessorialsFilterParams = {
  buttons: ['reset', 'apply'],
  valueFormatter: (params) =>
    params.value ? titleize(humanize(t(sluggify(params.value)))) : params.value,

  values: MISSING_ACCESSORIAL,
};

export const preNoticeAccessorialsFilterParams = {
  buttons: ['reset', 'apply'],
  valueFormatter: (params) =>
    params.value ? titleize(humanize(t(sluggify(params.value)))) : params.value,

  values: PRE_NOTICE_ACCESSORIAL_TYPES,
};

export const pendingAccessorialsFilterParams = {
  buttons: ['reset', 'apply'],
  valueFormatter: (params) =>
    params.value ? titleize(humanize(t(sluggify(params.value)))) : params.value,

  values: async (params) => {
    const data = await getAvailableAccessorials();
    params.success(data);
  },
};

const ITEMS_PER_PAGE_LIMIT = 10000;
async function fetchReferenceNumbersMasterList() {
  try {
    const skip = 0;
    const response = await getReferenceNumbersMasterList(
      ITEMS_PER_PAGE_LIMIT,
      skip,
    );
    const queryResult = response?.data?.getReferenceNumbersQuery;
    if (!queryResult) {
      throw new Error('No response from API');
    }
    const { reference_numbers } = queryResult;
    return (reference_numbers ?? []).map((refNum) => refNum.slug);
  } catch (_) {
    showToast(t('ERROR_FETCHING_DATA'), null, 'error');
  }
}

async function getAvailableAccessorials() {
  const data = await getAccessorials({ paginate: false });
  return (data?.entity?.items ?? []).map((item) => item.slug);
}

async function getAvailableDocumentTypes(listType) {
  try {
    const response = await getDocumentTypes(listType);
    const data = response?.data?.getAvailableDocumentTypes;
    if (!data) {
      throw new Error('No response from API');
    }
    return (data ?? []).map((item) => item.localizationSlug);
  } catch (e) {
    console.error(e);
    showToast(t('ERROR_FETCHING_DATA'), null, 'error');
  }
}

const POD_STATUS_LIST = ['verified', 'missing', 'invalid'];
const POD_STATUS_MAP = {
  verified: 'POD_STATUS_VERIFIED',
  unverified: 'POD_STATUS_UNVERIFIED',
  missing: 'POD_STATUS_MISSING',
  invalid: 'POD_STATUS_INVALID',
};

const SHIPPER_SUBSIDIARY_LIST = ['itg', ''];
const SHIPPER_SUBSIDIARY_MAP = {
  itg: 'ITG',
};

const CARRIER_SUBSIDIARY_LIST = [
  'itg',
  'atlas_marine',
  'three_rivers',
  'west_end',
  'xlr_8',
  '',
];
const CARRIER_SUBSIDIARY_MAP = {
  itg: 'ITG',
  atlas_marine: 'ATLAS_MARINE',
  three_rivers: 'THREE_RIVERS',
  west_end: 'WEST_END',
  xlr_8: 'XLR_8',
};

const YEARS_BACK = 1;

const translateFormatter = (cell) => {
  const { value } = cell;

  if (value || typeof value === 'boolean') {
    return t(sluggify(value.toString())) || null;
  }
  return cell.value || null;
};

const convertDateTimeWithTimezone = (
  date,
  dateFormat = null,
  timeFormat = null,
  timezone = null,
  showTimezone = false,
) => {
  return formatDate(date, {
    dateFormat,
    timeFormat,
    timezone,
    showTimezone,
    t,
  });
};

// Resolve the timezone to format a UV cell's date in. Stop-level columns
// (e.g. `loaded_delivery__0__stop_window_start`, `latest_completed__stop_window_start`)
// should render in the stop's local timezone rather than the order's, because
// stops can sit in a different timezone than the order's origin.
const getCellTimezone = (cell) => {
  const data = cell?.data || {};
  const field = cell?.colDef?.field || cell?.column?.getColId?.() || '';

  const stopInfo = getStopPrefixAndField(field);
  if (stopInfo.isStopGroupField) {
    const stopTz =
      data[
        getStopGroupFieldColId({
          prefix: stopInfo.prefix,
          stopGroupIndex: stopInfo.stopGroupIndex,
          field: 'stop_timezone',
        })
      ];
    if (stopTz) return stopTz;
  } else if (field.startsWith('latest_completed__')) {
    if (data.latest_completed__stop_timezone) {
      return data.latest_completed__stop_timezone;
    }
  }

  return data.order_timezone || DEFAULT_TIMEZONE;
};

export const dateFormatter = (cell) => {
  if (cell.data) {
    const { value } = cell;
    if (value) {
      return convertDateTimeWithTimezone(
        value,
        DEFAULT_DATE_FORMAT,
        null,
        getCellTimezone(cell),
        false,
      );
    }
  }
  return '';
};

const dateWithTimeFormatter = (cell) => {
  if (cell.data) {
    const { value } = cell;
    if (value) {
      return convertDateTimeWithTimezone(
        value,
        DEFAULT_DATE_FORMAT,
        DEFAULT_TIME_FORMAT,
        getCellTimezone(cell),
        true,
      );
    }
  }
  return '';
};

const nameRenderer = (param) => {
  if (param.value) return unescapeHTML(param.value);
  return null;
};

// minimal valid date for date filters
const minValidDate = format(
  toDate(yearsAgo(new Date(), YEARS_BACK)),
  'yyyy-MM-dd',
);

export const dateServerSideFilterParams = {
  maxNumConditions: 10,
  inRangeInclusive: true,
  buttons: ['reset', 'apply'],
  minValidDate,
  filterOptions: [
    {
      displayKey: 'equals',
      displayName: 'Equals',
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'notEqual',
      displayName: 'Not equal',
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'before',
      displayName: 'Before',
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'after',
      displayName: 'After',
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'inRange',
      displayName: 'Between',
      inRangeInclusive: true,
      numberOfInputs: 2,
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'blank',
      displayName: 'Blank',
      numberOfInputs: 0,
      predicate() {
        return true;
      },
    },
    {
      displayKey: 'notBlank',
      displayName: 'Not blank',
      numberOfInputs: 0,
      predicate() {
        return true;
      },
    },
    ...TIME_SELECTIONS.map((item) => ({
      displayKey: item.localizationSlug,
      displayName: item.name,
      numberOfInputs: 0,
      predicate() {
        return true;
      },
    })),
  ],
};

export const textDefaultFilterParams = {
  maxNumConditions: 10,
  buttons: ['reset', 'apply'],
  filterOptions: [
    'startsWith',
    {
      displayKey: 'notStartsWith',
      displayName: 'Does not start with',
      predicate: ([filterValue], cellValue) => {
        return !cellValue?.startsWith(filterValue);
      },
    },
    'endsWith',
    'equals',
    'notEqual',
    'blank',
    'notBlank',
  ],
};

export const booleanDefaultFilterParams = {
  buttons: ['reset', 'apply'],
  suppressMiniFilter: true,
  suppressSelectAll: true,
  values: [true, false],
};

const LOADED_PICKUP_COL_GROUP_BASE = {
  groupId: 'loaded_pickup_stops',
  headerName: 'Loaded Pickup',
  hide: false,
};

const LOADED_DELIVERY_COL_GROUP_BASE = {
  groupId: 'loaded_delivery_stops',
  headerName: 'Loaded Delivery',
  hide: false,
};

const EMPTY_DELIVERY_COL_GROUP_BASE = {
  groupId: 'empty_delivery_stops',
  headerName: 'Empty Delivery',
  hide: false,
};

const EMPTY_PICKUP_COL_GROUP_BASE = {
  groupId: 'empty_pickup_stops',
  headerName: 'Empty Pickup',
  hide: false,
};

const OFFSITE_LOADED_PICKUP_COL_GROUP_BASE = {
  groupId: 'offsite_loaded_pickup_stops',
  headerName: 'Offsite Loaded Pickup',
  hide: false,
};

const OFFSITE_LOADED_DELIVERY_COL_GROUP_BASE = {
  groupId: 'offsite_loaded_delivery_stops',
  headerName: 'Offsite Loaded Delivery',
  hide: false,
};

const EMPTY_OFFSITE_DELIVERY_COL_GROUP_BASE = {
  groupId: 'empty_offsite_delivery_stops',
  headerName: 'Offsite Empty Delivery',
  hide: false,
};

const EMPTY_OFFSITE_PICKUP_COL_GROUP_BASE = {
  groupId: 'empty_offsite_pickup_stops',
  headerName: 'Offsite Empty Pickup',
  hide: false,
};

const LATEST_COMPLETED_COL_GROUP_BASE = {
  groupId: 'latest_completed_stops',
  headerName: 'Latest completed stop',
  hide: false,
};

const STOP_TYPES_COL_GROUP_BASE_MAP = {
  loaded_pickup: LOADED_PICKUP_COL_GROUP_BASE,
  loaded_delivery: LOADED_DELIVERY_COL_GROUP_BASE,
  empty_delivery: EMPTY_DELIVERY_COL_GROUP_BASE,
  empty_pickup: EMPTY_PICKUP_COL_GROUP_BASE,
  offsite_loaded_pickup: OFFSITE_LOADED_PICKUP_COL_GROUP_BASE,
  offsite_loaded_delivery: OFFSITE_LOADED_DELIVERY_COL_GROUP_BASE,
  empty_offsite_delivery: EMPTY_OFFSITE_DELIVERY_COL_GROUP_BASE,
  empty_offsite_pickup: EMPTY_OFFSITE_PICKUP_COL_GROUP_BASE,
  latest_completed: LATEST_COMPLETED_COL_GROUP_BASE,
};

const STOP_SEQUENCE_COL_BASE = {
  filter: false,
  width: 100,
  cellDataType: 'number',
  type: 'rightAligned',
};

const STOP_ADDRESS_COL_BASE = {
  width: 400,
};

const STOP_DATE_WITH_TIMESTAMP_COL_BASE = {
  valueFormatter: dateWithTimeFormatter,
  filter: 'agDateColumnFilter',
  floatingFilter: true,
  width: 200,
  filterParams: dateServerSideFilterParams,
  cellDataType: 'dateString',
};

const STOP_WINDOW_START_COL_BASE = {
  valueFormatter: dateWithTimeFormatter,
  filter: 'agDateColumnFilter',
  floatingFilter: true,
  width: 200,
  filterParams: dateServerSideFilterParams,
  cellDataType: 'dateString',
};

const STOP_WINDOW_END_COL_BASE = {
  valueFormatter: dateWithTimeFormatter,
  filter: 'agDateColumnFilter',
  floatingFilter: true,
  width: 200,
  filterParams: dateServerSideFilterParams,
  cellDataType: 'dateString',
};

const STANDARD_REFERENCE_NUMBER_COL_BASE = {
  autoHeight: true,
  filter: TextFilterStartsWith,
  columnGroupShow: 'open',
  cellStyle: { whiteSpace: 'pre' },
};

const stopGroupFieldRegExp =
  /(?<prefix>[a-zA-Z_]+)__(?<stopGroupIndex>\d+)__(?<field>[a-zA-Z_]+)/g;

export function getStopGroupFieldColId({ prefix, stopGroupIndex, field }) {
  return `${prefix}__${stopGroupIndex}__${field}`;
}

export function getStopPrefixAndField(colId = '') {
  const matches = [...colId.matchAll(stopGroupFieldRegExp)];
  if (!matches[0]) {
    return {
      prefix: null,
      stopGroupIndex: null,
      field: null,
      isStopGroupField: false,
    };
  }

  const groups = matches[0]?.groups || {};
  const prefix = groups?.prefix;
  const stopGroupIndex = groups?.stopGroupIndex;
  const field = groups?.field;

  const parsedIndex = parseInt(stopGroupIndex, 10);
  const res = {
    prefix,
    stopGroupIndex: parsedIndex,
    field,
    isStopGroupField: !!prefix && !Number.isNaN(parsedIndex) && !!field,
  };

  return res;
}

const STOP_CONTAINER_COL_BASE = {
  filter: TextFilterStartsWith,
};

const STOP_EQUIPMENT_TYPE_COL_BASE = {
  headerName: 'Equipment Type',
  cellRenderer: translateFormatter,
};

const STOP_EQUIPMENT_SIZE_COL_BASE = {
  headerName: 'Equipment Size',
  type: 'rightAligned',
};

const STOP_CHASSIS_NUMBER_BASE = {
  filter: TextFilterStartsWith,
};
const STOP_STATUS_COL_BASE = {
  cellRenderer: statusFormatter,
  filter: 'agSetColumnFilter',
  filterParams: {
    buttons: ['reset', 'apply'],
    values: [
      'Unstarted',
      'Arriving',
      'Arrived',
      'Complete',
      'Canceled',
      'Attempt',
    ],
    // turn off standard sorting of values
    comparator: () => 1,
  },
};

const STOP_TYPE_COLUMN_BASE = {
  cellRenderer: statusFormatter,
  filter: 'agSetColumnFilter',
  filterParams: {
    buttons: ['reset', 'apply'],
    values: STOP_TYPES,
    valueFormatter: (params) =>
      params.value
        ? titleize(humanize(t(sluggify(params.value))))
        : params.value,
    // turn off standard sorting of values
    comparator: () => 1,
  },
};

function getStopFields(stopType, index, overridePrefix = '') {
  const prefix = overridePrefix.length
    ? overridePrefix
    : `${stopType}__${index}__`;

  const columnGroupShow = 'open';

  const fields = [
    {
      headerName: 'Sequence',
      field: `${prefix}stop_sequence`,
      ...STOP_SEQUENCE_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Name',
      field: `${prefix}stop_location_name`,
      ...STOP_ADDRESS_COL_BASE,
      filterParams: {
        ...textDefaultFilterParams,
      },
    },
    {
      headerName: 'Address',
      field: `${prefix}stop_address`,
      ...STOP_ADDRESS_COL_BASE,
      filterParams: {
        ...textDefaultFilterParams,
      },
    },
    {
      headerName: 'Window Start',
      field: `${prefix}stop_window_start`,
      ...STOP_WINDOW_START_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Window End',
      field: `${prefix}stop_window_end`,
      ...STOP_WINDOW_END_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Arrival Actual',
      field: `${prefix}stop_arrived_date`,
      ...STOP_DATE_WITH_TIMESTAMP_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Completed Actual',
      field: `${prefix}stop_completed_date`,
      ...STOP_DATE_WITH_TIMESTAMP_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Container',
      field: `${prefix}stop_equipment_id`,
      ...STOP_CONTAINER_COL_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Equipment Type',
      field: `${prefix}stop_equipment_type`,
      ...STOP_EQUIPMENT_TYPE_COL_BASE,
      columnGroupShow,
      filterParams: {
        ...textDefaultFilterParams,
      },
    },
    {
      headerName: 'Equipment Size',
      field: `${prefix}stop_equipment_size`,
      ...STOP_EQUIPMENT_SIZE_COL_BASE,
      columnGroupShow,
      filterParams: {
        ...textDefaultFilterParams,
      },
    },
    {
      headerName: 'Chassis Number',
      field: `${prefix}stop_chassis_number`,
      ...STOP_CHASSIS_NUMBER_BASE,
      columnGroupShow,
    },
    {
      headerName: 'Status',
      field: `${prefix}stop_status`,
      ...STOP_STATUS_COL_BASE,
      columnGroupShow,
    },
  ];

  if (prefix === LATEST_STOP_PREFIX) {
    fields.splice(1, 0, {
      headerName: 'Stop Type',
      field: `${prefix}stop_type`,
      ...STOP_TYPE_COLUMN_BASE,
      columnGroupShow,
    });
  }
  return fields;
}

const getLatestStopGroup = () => {
  const stopType = `latest_completed`;
  const children = getStopFields(stopType, 0, LATEST_STOP_PREFIX);
  const headerName = STOP_TYPES_COL_GROUP_BASE_MAP[stopType].headerName;

  return {
    groupId: `latest_completed`,
    hide: false,
    openByDefault: true,
    headerName: headerName,
    marryChildren: false,
    children,
  };
};

function getAdditionalStopGroups(stopType, stopsCount, getStopGroupFunction) {
  const children = Array.from({ length: stopsCount }).reduce(
    (stopGroups, _) => {
      if (stopType === 'latest_completed') {
        const group = getStopGroupFunction();
        return stopGroups.concat(group);
      }
    },
    [],
  );
  const group = {
    ...STOP_TYPES_COL_GROUP_BASE_MAP[stopType],
    openByDefault: true,
    marryChildren: false,
    children,
  };
  return group;
}

function getStopGroups(stopType, stopsCount) {
  const children = Array.from({ length: stopsCount }).reduce(
    (stopGroups, _, index) => {
      const children = getStopFields(stopType, index);
      const headerName = STOP_TYPES_COL_GROUP_BASE_MAP[stopType].headerName;

      const group = {
        groupId: `${stopType}__${index}`,
        hide: false,
        openByDefault: index === 0,
        headerName: `${headerName} ${index + 1}`,
        marryChildren: false,
        children,
      };

      return stopGroups.concat(group);
    },
    [],
  );
  const group = {
    ...STOP_TYPES_COL_GROUP_BASE_MAP[stopType],
    openByDefault: true,
    marryChildren: false,
    children,
  };
  return group;
}

const SHORTHAUL_STOP_TYPES = ['loaded_pickup', 'loaded_delivery'];

const DRAYAGE_STOP_TYPES = [
  ...SHORTHAUL_STOP_TYPES,
  'empty_pickup',
  'empty_delivery',
  'offsite_loaded_delivery',
  'offsite_loaded_pickup',
  'empty_offsite_pickup',
  'empty_offsite_delivery',
];

const DRAYAGE_STOPS_LIMIT = 3;
const ADDITIONAL_STOPS = [
  {
    stopType: 'latest_completed',
    stopLimit: 1,
    getStopGroupFunction: getLatestStopGroup,
  },
];

export function getAdditionalStopGroupsColumnDefs() {
  return ADDITIONAL_STOPS.map(({ stopType, stopLimit, getStopGroupFunction }) =>
    getAdditionalStopGroups(stopType, stopLimit || 1, getStopGroupFunction),
  );
}

export function getDrayageStopGroupsColumnDefs() {
  return DRAYAGE_STOP_TYPES.reduce(
    (stopsColGroups, stopType) =>
      stopsColGroups.concat(getStopGroups(stopType, DRAYAGE_STOPS_LIMIT)),
    [],
  );
}

const SHORTHAUL_STOPS_LIMIT = 1;
export function getShorthaulStopGroupsColumnDefs() {
  return SHORTHAUL_STOP_TYPES.reduce(
    (stopsColGroups, stopType) =>
      stopsColGroups.concat(getStopGroups(stopType, SHORTHAUL_STOPS_LIMIT)),
    [],
  );
}

export function getStandardReferenceNumbersFields(view = 'drayage') {
  const valueGetter = (params, field) => {
    if (params && params?.data && params?.data?.reference_numbers) {
      const {
        data: { reference_numbers: ref },
      } = params;

      const data = ref.filter((r) => {
        return (
          !has(r, 'stop_id') &&
          r.name === field &&
          STANDARD_REFERENCE_NUMBERS.includes(field)
        );
      });
      return data
        .map((r) => {
          return r.value || '';
        })
        .join('\n');
    }
    return null;
  };
  const cellRenderer = (param, field) => {
    if (param.data && param?.data?.reference_numbers) {
      const whitelist = param?.data?.reference_numbers.filter((f) => {
        return (
          [field].indexOf(f.name) > -1 &&
          STANDARD_REFERENCE_NUMBERS.includes(field)
        );
      });
      return whitelist
        .map((r) => {
          return r.value;
        })
        .join('\n');
    }
    return null;
  };

  return [
    {
      headerName: 'Delivery Order',
      field: 'delivery_order',
      valueGetter: (params) => valueGetter(params, 'delivery_order'),
      cellRenderer: (params) => cellRenderer(params, 'delivery_order'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Gate Dropoff',
      field: 'gate_dropoff',
      valueGetter: (params) => valueGetter(params, 'gate_dropoff'),
      cellRenderer: (params) => cellRenderer(params, 'gate_dropoff'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Gate Pickup',
      field: 'gate_pickup',
      valueGetter: (params) => valueGetter(params, 'gate_pickup'),
      cellRenderer: (params) => cellRenderer(params, 'gate_pickup'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'House Bill Number',
      field: 'house_bill_number',
      valueGetter: (params) => valueGetter(params, 'house_bill_number'),
      cellRenderer: (params) => cellRenderer(params, 'house_bill_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'ISA',
      field: 'isa',
      valueGetter: (params) => valueGetter(params, 'isa'),
      cellRenderer: (params) => cellRenderer(params, 'isa'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Master BOL',
      field: 'master_bol',
      valueGetter: (params) => valueGetter(params, 'master_bol'),
      cellRenderer: (params) => cellRenderer(params, 'master_bol'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'MG Load ID',
      field: 'mg_load_id',
      valueGetter: (params) => valueGetter(params, 'mg_load_id'),
      cellRenderer: (params) => cellRenderer(params, 'mg_load_id'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'MO Number',
      field: 'mo_number',
      valueGetter: (params) => valueGetter(params, 'mo_number'),
      cellRenderer: (params) => cellRenderer(params, 'mo_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'PU Number',
      field: 'pu_number',
      valueGetter: (params) => valueGetter(params, 'pu_number'),
      cellRenderer: (params) => cellRenderer(params, 'pu_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Seal Number',
      field: 'seal_number',
      valueGetter: (params) => valueGetter(params, 'seal_number'),
      cellRenderer: (params) => cellRenderer(params, 'seal_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Tracking ID',
      field: 'tracking_id',
      valueGetter: (params) => valueGetter(params, 'tracking_id'),
      cellRenderer: (params) => cellRenderer(params, 'tracking_id'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'TO',
      field: 'truck_order',
      valueGetter: (params) => valueGetter(params, 'truck_order'),
      cellRenderer: (params) => cellRenderer(params, 'truck_order'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'VRID',
      field: 'VRID',
      valueGetter: (params) => valueGetter(params, 'VRID'),
      cellRenderer: (params) => cellRenderer(params, 'VRID'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Work Order Number',
      field: 'work_order_number',
      valueGetter: (params) => valueGetter(params, 'work_order_number'),
      cellRenderer: (params) => cellRenderer(params, 'work_order_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Last Move',
      field: 'last_move',
      valueGetter: (params) => valueGetter(params, 'last_move'),
      cellRenderer: (params) => cellRenderer(params, 'last_move'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Final Destination',
      field: 'final_destination',
      valueGetter: (params) => valueGetter(params, 'final_destination'),
      cellRenderer: (params) => cellRenderer(params, 'final_destination'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Position',
      field: 'position',
      valueGetter: (params) => valueGetter(params, 'position'),
      cellRenderer: (params) => cellRenderer(params, 'position'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Cargo Weight (LB)',
      field: 'cargo_weight',
      valueGetter: (params) => valueGetter(params, 'cargo_weight'),
      cellRenderer: (params) => cellRenderer(params, 'cargo_weight'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'T State',
      field: 't_state',
      valueGetter: (params) => valueGetter(params, 't_state'),
      cellRenderer: (params) => cellRenderer(params, 't_state'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Freight Kind',
      field: 'freight_kind',
      valueGetter: (params) => valueGetter(params, 'freight_kind'),
      cellRenderer: (params) => cellRenderer(params, 'freight_kind'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Over Weight',
      field: 'over_weight',
      valueGetter: (params) => valueGetter(params, 'over_weight'),
      cellRenderer: (params) => cellRenderer(params, 'over_weight'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Requires Power',
      field: 'reqs_power',
      valueGetter: (params) => valueGetter(params, 'reqs_power'),
      cellRenderer: (params) => cellRenderer(params, 'reqs_power'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Unit Impediments',
      field: 'unit_impediments',
      valueGetter: (params) => valueGetter(params, 'unit_impediments'),
      cellRenderer: (params) => cellRenderer(params, 'unit_impediments'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Booking Number',
      field: 'booking_number',
      valueGetter: (params) => valueGetter(params, 'booking_number'),
      cellRenderer: (params) => cellRenderer(params, 'booking_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Partner Shipment Id',
      field: 'partnerShipmentId',
      valueGetter: (params) => valueGetter(params, 'partnerShipmentId'),
      cellRenderer: (params) => cellRenderer(params, 'partnerShipmentId'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Billing',
      field: 'Billing',
      valueGetter: (params) => valueGetter(params, 'Billing'),
      cellRenderer: (params) => cellRenderer(params, 'Billing'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Mode',
      field: 'Mode',
      valueGetter: (params) => valueGetter(params, 'Mode'),
      cellRenderer: (params) => cellRenderer(params, 'Mode'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Shipment',
      field: 'Shipment',
      valueGetter: (params) => valueGetter(params, 'Shipment'),
      cellRenderer: (params) => cellRenderer(params, 'Shipment'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'PO Number',
      field: 'po_number',
      valueGetter: (params) => valueGetter(params, 'po_number'),
      cellRenderer: (params) => cellRenderer(params, 'po_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'CS Number',
      field: 'CS NUMBER',
      valueGetter: (params) => valueGetter(params, 'CS NUMBER'),
      cellRenderer: (params) => cellRenderer(params, 'CS NUMBER'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Category',
      field: 'category',
      valueGetter: (params) => valueGetter(params, 'category'),
      cellRenderer: (params) => cellRenderer(params, 'category'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Peel',
      field: 'peel',
      valueGetter: (params) => valueGetter(params, 'peel'),
      cellRenderer: (params) => cellRenderer(params, 'peel'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'LCL',
      field: 'lcl',
      valueGetter: (params) => valueGetter(params, 'lcl'),
      cellRenderer: (params) => cellRenderer(params, 'lcl'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Bobtail',
      field: 'bobtail',
      valueGetter: (params) => valueGetter(params, 'bobtail'),
      cellRenderer: (params) => cellRenderer(params, 'bobtail'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Service Type',
      field: 'Service Type',
      valueGetter: (params) => valueGetter(params, 'Service Type'),
      cellRenderer: (params) => cellRenderer(params, 'Service Type'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Appointment Number',
      field: 'appointment_number',
      valueGetter: (params) => valueGetter(params, 'appointment_number'),
      cellRenderer: (params) => cellRenderer(params, 'appointment_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    {
      headerName: 'Invoice Number',
      field: 'invoice_number',
      valueGetter: (params) => valueGetter(params, 'invoice_number'),
      cellRenderer: (params) => cellRenderer(params, 'invoice_number'),
      ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    },
    ...(view === 'drayage'
      ? [
          {
            headerName: 'Credit Memo Number',
            field: 'credit_memo_number',
            valueGetter: (params) => valueGetter(params, 'credit_memo_number'),
            cellRenderer: (params) =>
              cellRenderer(params, 'credit_memo_number'),
            ...STANDARD_REFERENCE_NUMBER_COL_BASE,
            comparator: () => 0,
          },
        ]
      : []),
  ];
}

export function getBridgeReasonCodeGroup() {
  if (!featureEnabled(CONFIGURED_FEATURES.BULK_MARKET_EXCEPTIONS)) return [];
  const columns = {
    openByDefault: true,
    hide: false,
    marryChildren: false,
    priority: 0,
    headerName: 'BRIDGE_AND_REASON_CODES',
    groupId: 'bridge_reason_code',
    children: [
      {
        headerName: 'Shipment Leg',
        field: 'market_exceptions_shipment_leg',
        cellRenderer: ShipmentLegs,
        editable: true,
        cellEditor: ShipmentLegEditor,
        minWidth: 250,
        wrapText: true,
        autoHeight: true,
        enableRowGroup: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
          valueFormatter: (params) =>
            params.value
              ? titleize(humanize(t(sluggify(params.value))))
              : params.value,
          values: async (params) => {
            const data = await getBridgeCodeFilters('shipment_leg');
            params.success(data || []);
          },
        },
      },
      {
        headerName: 'Bridge',
        field: 'market_exceptions_bridge_code',
        cellRenderer: ({ value }) => (
          <HoverBadges value={value} doSluggify={false} />
        ),
        minWidth: 250,
        wrapText: true,
        autoHeight: true,
        enableRowGroup: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
          valueFormatter: (params) =>
            params.value
              ? titleize(humanize(t(sluggify(params.value))))
              : params.value,
          values: async (params) => {
            const data = await getBridgeCodeFilters('bridge_code');
            params.success(data || []);
          },
        },
      },
      {
        headerName: 'Reason Code',
        field: 'market_exceptions_reason_code',
        cellRenderer: ({ value }) => (
          <HoverBadges value={value} doSluggify={false} />
        ),
        minWidth: 300,
        wrapText: true,
        autoHeight: true,
        enableRowGroup: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
          valueFormatter: (params) =>
            params.value
              ? titleize(humanize(t(sluggify(params.value))))
              : params.value,
          values: async (params) => {
            const data = await getBridgeCodeFilters('reason_code');
            params.success(data || []);
          },
        },
      },
      {
        headerName: 'Notes',
        field: 'market_exceptions_reason_description',
        ...STANDARD_REFERENCE_NUMBER_COL_BASE,
        filter: false,
      },
    ],
  };
  return [columns];
}

export function getBillingStatusStateGroup() {
  const carrierMandatoryColumns = {
    priority: 2,
    headerName: 'Carrier Mandatory Documents',
    groupId: 'billing_status_state',
    hide: false,
    marryChildren: false,
    children: [
      {
        headerName: 'Carrier Required Docs',
        field: 'billing_status_state_has_missing_carrier_docs',
        cellRenderer: checkedFormatter,
        filter: 'agSetColumnFilter',
        filterParams: booleanDefaultFilterParams,
      },
      {
        headerName: 'Carrier Missing Docs',
        field: 'billing_status_state_missing_carrier_docs',
        cellRenderer: HoverBadges,
        minWidth: 300,
        enableRowGroup: false,
        wrapText: true,
        autoHeight: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
          valueFormatter: (params) => {
            return params.value
              ? titleize(humanize(t(sluggify(params.value))))
              : params.value;
          },
          values: async (params) => {
            const data = await getAvailableDocumentTypes('shipment');

            params.success(
              (data || []).map((docType) => docType.toLowerCase()),
            );
          },
        },
      },
    ],
  };
  const defaultBillingColumns = [
    {
      priority: 0,
      headerName: 'Billing Status',
      groupId: 'billing_status',
      hide: false,
      marryChildren: false,
      children: [
        {
          headerName: 'Billing Status',
          field: 'billing_status_state_billing_status',
          cellRenderer: ({ value }) => {
            if (!value) return null;
            return <BillingStatusBadge billingStatus={value} />;
          },
          filter: 'agSetColumnFilter',
          filterParams: {
            buttons: ['reset', 'apply'],
            valueFormatter: ({ value }) =>
              value ? titleize(humanize(t(value))) : '(Blank)',
            values: ['', ...billingStatusList],
          },
          autoHeight: true,
        },
        {
          headerName: 'Billing Status Updated At',
          field: 'billing_status_state_billing_status_modified_at',
          filter: 'agDateColumnFilter',
          valueFormatter: dateFormatter,
          isDate: true,
          filterParams: {
            ...dateServerSideFilterParams,
          },
          floatingFilter: true,
          sortingOrder: ['desc', 'asc'],
        },
        {
          groupId: 'override_billing_status',
          hide: false,
          headerName: 'Override Billing Status',

          children: [
            {
              headerName: 'Note',
              field: 'pause_automated_billing_evaluation_reason_code',
            },
            {
              headerName: 'Reason Codes',
              minWidth: 250,
              field: 'pause_automated_billing_evaluation_reason_codes',
              cellRenderer: ({ value, node, column, api }) => {
                const handleClick = (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  api.startEditingCell({
                    rowIndex: node.rowIndex,
                    colKey: column.getId(),
                  });
                };
                const showEditButton =
                  SessionStore.isBillingStatusAdmin() ||
                  SessionStore.isBillingStatusManager() ||
                  SessionStore.isBillingStatusUser();
                return (
                  <div className="flex justify-between">
                    {value && (
                      <ul className="flex-1">
                        {(value ?? []).map((item) => (
                          <li key={item}>{t(item)}</li>
                        ))}
                      </ul>
                    )}
                    {(value?.length ?? 0) === 0 && <span>&nbsp;</span>}
                    {showEditButton && (
                      <EditButton
                        className="flex-shrink-0 flex-grow-0"
                        size="small"
                        onClick={handleClick}
                      />
                    )}
                  </div>
                );
              },
              cellEditor: BillingStatusPendingReviewEditor,
              filter: 'agSetColumnFilter',
              filterParams: {
                buttons: ['reset', 'apply'],
                valueFormatter: (params) =>
                  params.value ? t(params.value) : params.value,

                values: [null, ...overrideBillingStatusReasonCodes],
              },
              autoHeight: true,
              editable: true,
            },
          ],
        },
        {
          headerName: 'Billable Event',
          field: 'billing_status_billable_event',
          cellRenderer: ({ value }) => {
            return <Badge color="green">{titleize(humanize(t(value)))}</Badge>;
          },
          filter: 'agSetColumnFilter',
          filterParams: {
            buttons: ['reset', 'apply'],
            valueFormatter: ({ value }) => {
              return titleize(humanize(t(value)));
            },
            values: billableEventList,
          },
          autoHeight: true,
        },
        {
          headerName: 'Invoice Grouping',
          field: 'billing_status_invoice_grouping',
          cellRenderer: ({ value }) => {
            return <Badge color="green">{titleize(humanize(t(value)))}</Badge>;
          },
          filter: 'agSetColumnFilter',
          filterParams: {
            buttons: ['reset', 'apply'],
            valueFormatter: ({ value }) => {
              return titleize(humanize(t(value)));
            },
            values: invoiceGroupingFilterList,
          },
          autoHeight: true,
        },
        {
          headerName: 'Pending accessorial Evaluation',
          field: 'billing_status_pending_accessorial_evaluation',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
      ],
    },
    {
      priority: 1,
      headerName: 'Missing for Ready to Bill',
      groupId: 'billing_status_state',
      hide: false,
      marryChildren: false,
      children: [
        {
          headerName: 'Has Billable Charge',
          field: 'billing_status_state_has_pending_shipper_unbilled_charge',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Unbilled Charges',
          field: 'unbilled_charges',
          cellRenderer: ({ data }) => {
            if (!data?.unbilled_charges) {
              return '—';
            }
            return formatMoney(data?.unbilled_charges, data?.shipment_currency);
          },
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'number',
          type: 'numericColumn',
          ...getNumberFilter(),
        },
        {
          headerName: 'Required Docs',
          field: 'billing_status_state_has_required_docs',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Missing Docs',
          field: 'billing_status_state_missing_docs',
          cellRenderer: HoverBadges,
          minWidth: 300,
          enableRowGroup: false,
          wrapText: true,
          autoHeight: true,
          filter: 'agSetColumnFilter',
          filterParams: documentsFilterParams,
        },
        {
          headerName: 'Required Ref Nums',
          field: 'billing_status_state_has_required_reference_numbers',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Missing Ref Nums',
          field: 'billing_status_state_missing_reference_numbers',
          cellRenderer: HoverBadges,
          minWidth: 350,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          filter: 'agSetColumnFilter',
          filterParams: missingReferenceNumbersFilterParams,
        },
        {
          headerName: 'Minimum Missing Accessorials',
          field: 'billing_status_state_has_missing_required_accessorials',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Minimum Pending Accessorials',
          field: 'billing_status_state_has_pending_required_accessorials',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Missing Accessorials',
          field: 'billing_status_state_missing_required_accessorial_types',
          cellRenderer: HoverBadges,
          minWidth: 100,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          filter: 'agSetColumnFilter',
          filterParams: missingAccessorialsFilterParams,
        },
        {
          headerName: 'Pending Accessorials',
          field: 'billing_status_state_pending_required_accessorial_types',
          cellRenderer: HoverBadges,
          minWidth: 350,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          filter: 'agSetColumnFilter',
          filterParams: pendingAccessorialsFilterParams,
        },
        {
          headerName: 'Billable Stops Completed',
          field: 'billing_status_state_has_required_billable_stops',
          cellRenderer: checkedFormatter,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          cellDataType: 'boolean',
          filter: 'agSetColumnFilter',
          filterParams: booleanDefaultFilterParams,
        },
        {
          headerName: 'Incomplete Billable Stops',
          field: 'billing_status_state_pending_billable_stops',
          minWidth: 350,
          cellRenderer: HoverBadges,
          wrapText: true,
          autoHeight: true,
          enableRowGroup: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            valueFormatter: ({ value }) => t(sluggify(value)),
            buttons: ['reset', 'apply'],
            values: STOP_TYPES,
          },
        },
      ],
    },
  ];
  if (featureEnabled(CONFIGURED_FEATURES.CARRIER_MANDATORY_STOP_DOCUMENTS)) {
    defaultBillingColumns.push(carrierMandatoryColumns);
  }
  return defaultBillingColumns;
}

export function getReferenceNumberGroup(view = 'drayage') {
  return [
    {
      headerName: 'Standard Reference Numbers',
      groupId: 'standard_reference_numbers',
      hide: false,
      marryChildren: false,
      children: getStandardReferenceNumbersFields(view),
    },
  ];
}

export function getDrayageShipmentTypes() {
  return {
    headerName: 'Shipment Type',
    field: 'shipment_type',
    priority: 8,
    width: 100,
    cellRenderer: statusFormatter,
    filter: true,
    filterParams: {
      buttons: ['reset', 'apply'],
      values: ['drayage', 'rail'],
      suppressSorting: true,
      defaultToNothingSelected: false,
    },
  };
}

export const moneyValueRenderer = (params) => {
  return params.valueFormatted || '';
};

const getBaseColumnDefs = (view, includeAmazonPreArrival = false) => [
  {
    headerName: 'POD Status',
    field: 'pod_status',
    valueFormatter: (params) =>
      params.value ? t(POD_STATUS_MAP[params.value]) : '-',
    editable: true,
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: POD_STATUS_LIST,
    },
    updateFunc: onUpdatePODStatus,
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: (params) =>
        params.value ? t(POD_STATUS_MAP[params.value]) : '-',
      values: POD_STATUS_LIST,
    },
  },
  {
    headerName: 'Driver Phone Number',
    field: 'driver_phone_number',
    filter: false,
    valueFormatter: phoneFormatter,
    suppressFiltersToolPanel: false,
  },
  {
    headerName: 'Tags',
    field: 'tags',
    filter: TextFilterStartsWithNotStarts,
    cellRenderer: tagFormatter,
    editable: true,
    cellEditorPopup: true,
    cellEditor: TagsEditor,
    width: 200,
    titleize: true,
    autoHeight: true,
    cellStyle: { 'white-space': 'normal' },
    cellEditorParams: { onShipmentChangeTags },
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Mode',
    field: 'mode',
    titleize: true,
    cellRenderer: translateFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Move Type',
    field: 'move_type',
    cellRenderer: translateFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Special Instructions',
    field: 'notes',
    autoHeight: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Admin Notes',
    field: 'admin_notes',
    editable: true,
    updateFunc: onAdminNotesEdit,
    cellEditor: 'agLargeTextCellEditor',
    cellEditorPopup: true,
    autoHeight: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Cargo Description',
    field: 'cargo_description',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Hazmat Description',
    field: 'hazmat_description',
    autoHeight: true,
    cellRenderer: (data) => {
      const { data: shipment } = data;
      const { hazmat_details = [], hazmat_description = '' } = shipment || {};
      if (!hazmat_details?.length) {
        return t(hazmat_description);
      }
      return (
        <div className="flex justify-between">
          <div className="flex gap-2 py-2">
            <div
              data-testid="hazmat_description-list"
              className="flex flex-wrap gap-2 flex-col"
            >
              {hazmat_details?.map((item, index) => {
                const { hazmat_description = '' } = item;
                return (
                  <div
                    title={hazmat_description}
                    data-testid={`hazmat_description-list-${index}`}
                    key={`hazmat_description_key_${hazmat_description}`}
                    color={'gray'}
                    className="!min-w-fit"
                  >
                    {titleize(t(hazmat_description))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    },
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Origin',
    field: 'shipment_origin',
    priority: 35,
    titleize: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Origin Name',
    field: 'shipment_origin_name',
    cellRenderer: nameRenderer,
    priority: 35,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Origin City',
    field: 'shipment_origin_city',
    priority: 35,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Origin State',
    field: 'shipment_origin_state',
    priority: 35,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Destination',
    field: 'shipment_destination',
    priority: 40,
    titleize: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Destination Name',
    field: 'shipment_destination_name',
    cellRenderer: nameRenderer,
    priority: 40,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Destination City',
    field: 'shipment_destination_city',
    priority: 40,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Destination State',
    field: 'shipment_destination_state',
    priority: 40,
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipment Type',
    field: 'shipment_type',
    priority: 8,
    width: 100,
    cellRenderer: statusFormatter,
    filter: true,
    filterParams: {
      buttons: ['reset', 'apply'],
      values: ['shorthaul', 'longhaul', 'dayrate'],
      suppressSorting: true,
      defaultToNothingSelected: false,
    },
  },
  {
    headerName: 'Hazmat Class',
    field: 'hazmat_class',
    capitalize: true,
    autoHeight: true,
    cellRenderer: (data) => {
      const { data: shipment } = data;
      const { hazmat_details = [], hazmat_class = '' } = shipment || {};
      if (!hazmat_details?.length) {
        return t(hazmat_class);
      }
      return (
        <div className="flex justify-between">
          <div className="flex gap-2 py-2">
            <div
              data-testid="hazmat_class"
              className="flex flex-wrap gap-2 flex-col"
            >
              {hazmat_details?.map((item, index) => {
                const { hazmat_class = '' } = item;
                return (
                  <div
                    data-testid={`hazmat_class-${index}`}
                    key={`hazmat_class_${hazmat_class}`}
                    color={'gray'}
                    className="!min-w-fit"
                  >
                    {t(item.hazmat_class)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    },
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Originating',
    field: 'originating',
    titleize: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Service Type',
    field: 'service_type',
    cellRenderer: translateFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Load',
    titleize: true,
    field: 'load_details',
    autoHeight: true,
    priority: 45,
    cellRenderer: (param) => {
      const loadTypes = ['pallets', 'boxes'];

      if (param.value) {
        const splitted = param.value
          .replace('\n', '<br/>')
          .toString()
          .split(' ');

        return splitted
          .map((str) => {
            if (loadTypes.includes(str.toLowerCase())) {
              return t(sluggify(str));
            }
            return str;
          })
          .join(' ');
      }
      return '';
    },
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Reference Numbers',
    field: 'reference_numbers',
    autoHeight: true,
    priority: 7,
    valueGetter: (params) => {
      if (params && params.data && params?.data?.reference_numbers) {
        const {
          data: { reference_numbers: ref },
        } = params;

        const data = ref.filter((r) => {
          return !has(r, 'stop_id');
        });
        // exclude reference numbers that have their own columns
        const blacklist = [
          'order_id',
          'shipment_id',
          'equipment_id',
          'vessel_or_rail',
        ];
        return data
          .filter(
            (f) =>
              !blacklist.includes(f.name) &&
              !STANDARD_REFERENCE_NUMBERS.includes(f.name),
          )
          .map((r) => {
            return `${titleize(humanize(r.name))} ${r.value || ''}`;
          })
          .join('\n');
      }
      return null;
    },
    cellRenderer: (param) => {
      if (param.data && param.data[param.column.colId]) {
        // exclude reference numbers that have their own columns
        const blacklist = [
          'order_id',
          'shipment_id',
          'equipment_id',
          'vessel_or_rail',
        ];
        // exclude also standard reference numbers that have separate column group
        const whitelist = param.data[param.column.colId].filter(
          (f) =>
            !blacklist.includes(f.name) &&
            !STANDARD_REFERENCE_NUMBERS.includes(f.name),
        );
        const items = whitelist.map((r, index) => {
          const label = titleize(humanize(t(sluggify(r.name))));
          return (
            <DescriptionListItem
              key={`DescriptionListItem-5642de74-${index}-${r}`}
              dataTestId={`DescriptionListItem-5642de74-${index}`}
              className="mb-2"
              label={label}
            >
              {r.value}
            </DescriptionListItem>
          );
        });
        return (
          <CargomaticTooltip
            dataTestId="CargomaticTooltip-b9ff2365"
            body={() => items.slice(0, 2)}
            position="top"
          >
            <div data-testid="div-698dd925">{items}</div>
          </CargomaticTooltip>
        );
      }
      return null;
    },
    filter: TextFilterStartsWith,
  },
  {
    headerName: 'Hazmat Packaging',
    field: 'hazmat_packaging_group',
    capitalize: true,
    autoHeight: true,
    cellRenderer: (data) => {
      const { data: shipment } = data;
      const { hazmat_details = [], hazmat_packaging_group = '' } =
        shipment || {};
      if (!hazmat_details?.length) {
        return t(hazmat_packaging_group);
      }
      return (
        <div className="flex justify-between">
          <div className="flex gap-2 py-2">
            <div
              data-testid="hazmat_packaging_group-list"
              className="flex flex-wrap gap-2 flex-col"
            >
              {hazmat_details?.map((item, index) => {
                const { hazmat_packaging_group = '' } = item;
                return (
                  <div
                    data-testid={`hazmat_packaging_group-${index}`}
                    key={`hazmat_packaging_group_${hazmat_packaging_group}`}
                    color={'gray'}
                    className="!min-w-fit"
                  >
                    {t(hazmat_packaging_group)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    },
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'SteamShip',
    field: 'steamship_or_railroad',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Railroads',
    field: 'railroads',
    cellRenderer: HoverBadges,
    minWidth: 300,
    enableRowGroup: false,
    wrapText: true,
    autoHeight: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Pre-Notice Accessorials',
    field: 'pre_notice_accessorials',
    cellRenderer: HoverBadges,
    minWidth: 350,
    wrapText: true,
    autoHeight: true,
    enableRowGroup: false,
    filter: 'agSetColumnFilter',
    filterParams: preNoticeAccessorialsFilterParams,
  },
  {
    headerName: 'Review Date',
    field: 'review_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'review_date',
    editable: true,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Invoice Cliff Date',
    field: 'invoice_cliff_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'invoice_cliff_date',
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Original Dispatch Date',
    field: 'original_dispatch_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'original_dispatch_date',
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Current Dispatch Date',
    field: 'current_dispatch_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'current_dispatch_date',
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Pickup from Exam Site',
    field: 'pickup_from_exam_site',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'pickup_from_exam_site',
    editable: true,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Dropoff at Exam Site',
    field: 'dropoff_at_exam_site',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'dropoff_at_exam_site',
    editable: true,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Accounting Review Date',
    field: 'accounting_review_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'accounting_review_date',
    editable: true,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Arrived Date',
    field: 'arrival_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    schema_value: 'arrival_date',
    editable: false,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Discharge Date',
    field: 'discharge_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'discharge_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Available Date',
    field: 'available_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'available_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Outgated Date',
    field: 'outgated_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'outgated_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Empty Ready for Pickup Date',
    field: 'empty_ready_for_pickup',
    filter: 'agDateColumnFilter',
    cellDataType: 'dateString',
    valueFormatter: dateFormatter,
    schema_value: 'empty_ready_for_pickup',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Empty Returned Date',
    field: 'empty_returned_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'empty_returned_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Last Cut Date',
    field: 'last_cut_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'last_cut_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Earliest Return Date',
    field: 'earliest_return_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'earliest_return_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Earliest Date to Get Empty',
    field: 'earliest_empty_date',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'earliest_empty_date',
    filterParams: dateServerSideFilterParams,
    editable: false,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Weight',
    titleize: true,
    field: 'total_weight',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
  },
  {
    headerName: 'Total Weight',
    field: 'shipment_weight',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueGetter: (params) => {
      const val = params.data?.shipment_weight;
      return val != null ? Number(val) : null;
    },
    valueFormatter: weightRenderer,
  },
  {
    headerName: 'Total Distance',
    titleize: true,
    field: 'distance',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueGetter: (params) => {
      const val = params.data?.distance;
      return val != null ? Number(val) : null;
    },
    valueFormatter: distanceFormatter,
  },
  {
    headerName: 'Leg Number',
    field: 'leg_number',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueGetter: (params) => {
      const val = params.data?.leg_number;
      return val != null ? Number(val) : null;
    },
  },
  {
    headerName: 'Leg Type',
    field: 'leg_type',
    cellRenderer: legTypeRenderer,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: ({ value }) =>
        value ? titleize(humanize(t(value))) : '(Blank)',
      values: ['', ...legTypeList],
    },
  },
  {
    headerName: 'Hub Type',
    field: 'hub_type',
    filterParams: {
      ...textDefaultFilterParams,
    },
    cellRenderer: (cell) => {
      if (!cell.value) return null;
      return titleize(cell.value.replace(/_/g, ' '));
    },
  },
  {
    headerName: 'Required Docs',
    field: 'has_required_docs',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Shipment ID',
    field: 'shipment_id',
    width: 150,
    pinnedFor: ['shorthaul', 'drayage'],
    filter: TextFilterStartsWith,
    headerCheckboxSelection: true,
    checkboxSelection: true,
    priority: 0,
    cellRenderer: shipmentIDRenderer,
  },
  {
    headerName: 'Shipment Status',
    field: 'shipmentStatus',
    pinnedFor: ['shorthaul', 'drayage'],
    cellRenderer: statusFormatter,
    filter: true,
    filterParams: {
      buttons: ['reset', 'apply'],
      values: shipmentStatuses.map((shipmentStatus) => shipmentStatus.message),
      suppressSorting: true,
    },
    floatingFilter: true,
    priority: 5,
  },
  {
    headerName: 'Order ID',
    field: 'order_id',
    width: 180,
    priority: 10,
    filter: TextFilterStartsWith,
    cellRenderer: orderIDRenderer,
  },
  {
    headerName: 'Order Status',
    field: 'orderStatus',
    width: 125,
    cellRenderer: statusFormatter,
    filter: TextFilterStartsWithNotStarts,
    priority: 15,
  },
  {
    headerName: 'Created By',
    field: 'shipment_created_by',
    width: 150,
    priority: 10,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Container Number',
    field: 'equipment_id',
    valueGetter: (params) => {
      if (params?.data?.reference_numbers) {
        return params?.data?.reference_numbers
          .filter((r) => r.name === 'equipment_id')
          .map((r) => r.optimized || r.value || '')
          .join('\n');
      }
      return '';
    },
    ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    priority: 16,
  },
  {
    headerName: 'Chassis Number',
    field: 'chassis_number',
    valueGetter: (params) => {
      if (params?.data?.reference_numbers) {
        return params?.data?.reference_numbers
          .filter((r) => r.name === 'chassis_number')
          .map((r) => r.optimized || r.value || '')
          .join('\n');
      }
      return '';
    },
    ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    priority: 17,
  },
  {
    headerName: 'Trailer Number',
    field: 'trailer_number',
    valueGetter: (params) => {
      if (params?.data?.reference_numbers) {
        return params?.data?.reference_numbers
          .filter((r) => r.name === 'trailer_number')
          .map((r) => r.optimized || r.value || '')
          .join('\n');
      }
      return '';
    },
    ...STANDARD_REFERENCE_NUMBER_COL_BASE,
    priority: 18,
  },
  {
    headerName: 'Order Type',
    field: 'order_type',
    width: 125,
    priority: 20,
    filterParams: {
      ...textDefaultFilterParams,
    },
    cellRenderer: translateFormatter,
  },
  {
    headerName: 'Margin',
    titleize: true,
    field: 'margin',
    cellRenderer: marginFormatter,
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    priority: 100,
  },
  {
    headerName: 'Market',
    titleize: true,
    field: 'marketCode',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      values: async (params) => {
        const marketCodes = await getUniqueMarketCodes();
        params.success([...marketCodes, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Opentrack Status Container 1',
    field: 'opentrack_container_status',
    width: 125,
    cellRenderer: portIntegrationsFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: (params) =>
        params.value
          ? titleize(humanize(t(sluggify(params.value))))
          : params.value,
      values: async (params) => {
        const openTrackStatuses = await getUniqueStatusList();
        params.success([...openTrackStatuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Opentrack Status Container 2',
    field: 'opentrack_container_status_2',
    width: 125,
    cellRenderer: portIntegrationsFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: (params) =>
        params.value
          ? titleize(humanize(t(sluggify(params.value))))
          : params.value,
      values: async (params) => {
        const openTrackStatuses = await getUniqueStatusList();
        params.success([...openTrackStatuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Opentrack Tracking Status',
    field: 'opentrack_tracking_status',
    width: 125,
    cellRenderer: opentrackTrackingStatusFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: (params) =>
        params.value
          ? titleize(humanize(t(sluggify(params.value))))
          : params.value,
      values: (params) => {
        const removedStatus = [
          OPENTRACK_TRACKING_STATUS.TRACKING_STOPPED,
          OPENTRACK_TRACKING_STATUS.TRACKING_INITIATED,
        ];
        const openTrackStatuses = Object.values(
          OPENTRACK_TRACKING_STATUS,
        ).filter((s) => !removedStatus.includes(s));
        params.success([...openTrackStatuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Validated BOL',
    field: 'master_bol_status',
    width: 125,
    cellRenderer: masterBolStatusFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: (params) =>
        params.value
          ? titleize(humanize(t(sluggify(params.value))))
          : params.value,
      values: (params) => {
        const masterBolStatuses = Object.values(MASTER_BOL_STATUS);
        params.success([...masterBolStatuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'Container Status',
    field: 'container_status',
    width: 125,
    cellRenderer: portIntegrationsFormatter,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      values: async (params) => {
        const statuses = await getContainerStatuses();
        params.success([...statuses, null]);
      },
    },
    floatingFilter: true,
  },
  {
    headerName: 'DO Status',
    field: 'delivery_order_status',
    cellRenderer: deliveryOrderFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'PU#',
    field: 'pickup_number',
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'SO#',
    field: 'so_number',
    hide: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipper',
    titleize: true,
    field: 'shipper_name',
    cellRenderer: nameRenderer,
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: CustomerMultiSearchFilter,
          filterParams: {
            // supressing FE filtering because of multifilter usage
            isUsedInMultifilter: true,
          },
        },
        {
          filter: TextFilterStartsWithNotStarts,
          filterParams: {
            // supressing FE filtering because of multifilter usage
            isUsedInMultifilter: true,
          },
        },
      ],
    },
  },
  {
    headerName: 'Shipper Subsidiary',
    field: 'shipper_subsidiary',
    valueFormatter: (params) =>
      params.value ? t(SHIPPER_SUBSIDIARY_MAP[params.value]) : '',
    editable: false,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: ({ value }) => {
        if (value === null) return `(${t('BLANK')})`;
        return t(SHIPPER_SUBSIDIARY_MAP[value]);
      },
      values: SHIPPER_SUBSIDIARY_LIST,
    },
  },
  {
    headerName: 'Carrier Subsidiary',
    field: 'carrier_subsidiary',
    valueFormatter: (params) =>
      params.value ? t(CARRIER_SUBSIDIARY_MAP[params.value]) : '',
    editable: false,
    filter: true,
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: ({ value }) => {
        if (value === null) return `(${t('BLANK')})`;
        return t(CARRIER_SUBSIDIARY_MAP[value]);
      },
      values: CARRIER_SUBSIDIARY_LIST,
    },
  },
  {
    headerName: 'Driver Type',
    cellRenderer: translateFormatter,
    field: 'carrier_driver_type',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Terminal Hold Status',
    minWidth: 250,
    field: 'terminal_hold_status',
    cellRenderer: ({ value, node, column, api }) => {
      const handleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        api.startEditingCell({
          rowIndex: node.rowIndex,
          colKey: column.getId(),
        });
      };
      return (
        /**
         * Render the Terminal Hold Status Column
         */
        <div className="flex justify-between">
          <div className="flex gap-2 py-2">
            <div
              data-testid="terminal-hold-status-list"
              className="flex flex-wrap gap-2"
            >
              {(value ?? []).map((item, index) => (
                <Badge
                  data-testid={`terminal-hold-status-list-${index}`}
                  key={item}
                  color={'gray'}
                  className="!min-w-fit"
                >
                  {titleize(t(sluggify(item)))}
                </Badge>
              ))}
            </div>
          </div>
          {(value?.length ?? 0) === 0 && <span>&nbsp;</span>}
          <EditButton
            className="flex-shrink-0 flex-grow-0"
            size="small"
            onClick={handleClick}
          />
        </div>
      );
    },
    cellEditor: TerminalHoldStatusEditor,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: (params) =>
        params.value ? t(sluggify(params.value)) : params.value,

      values: [null, ...TERMINAL_HOLD_STATUS_VALUES],
    },
    autoHeight: true,
    editable: true,
  },
  {
    headerName: 'Shipper Ops Group',
    titleize: true,
    field: 'shipper_operations_group',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      defaultToNothingSelected: true,
      suppressMiniFilter: true,
      suppressSelectAll: true,
      values: [...OPS_GROUP_OPTIONS.map((option) => option.slug), null],
    },
  },
  {
    headerName: 'Shipper Ops Manager',
    titleize: true,
    field: 'shipper_operations_manager',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'BCO',
    field: 'bco',
    width: 100,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Order Carrier',
    field: 'order_carrier_name',
    cellRenderer: nameRenderer,
    priority: 80,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipment Carrier',
    field: 'carrier_name',
    cellRenderer: nameRenderer,
    priority: 80,
    filterParams: {
      ...textDefaultFilterParams,
      filterOptions: [
        'startsWith',
        {
          displayKey: 'notStartsWith',
          displayName: 'Does not start with',
          predicate: ([filterValue], cellValue) => {
            return !cellValue?.startsWith(filterValue);
          },
        },
        'endsWith',
        'equals',
        'notEqual',
        'blank',
        'notBlank',
      ],
    },
  },
  {
    headerName: 'Driver',
    titleize: true,
    field: 'driver_name',
    priority: 55,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Vessel',
    field: 'vessel_or_rail',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Train',
    field: 'train',
    filterParams: {
      ...textDefaultFilterParams,
    },
    titleize: true,
  },
  {
    headerName: 'Chassis In Gate',
    field: 'chassis_in_gate',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
    schema_value: 'chassis_in_gate',
    editable: true,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Chassis Out Gate',
    field: 'chassis_out_gate',
    cellDataType: 'dateString',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
    schema_value: 'chassis_out_gate',
    editable: true,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Terminal LFD',
    field: 'lfd',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'last_free_date',
    filterParams: dateServerSideFilterParams,
    cellDataType: 'dateString',
    editable: false,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Custom LFD',
    field: 'custom_lfd',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'custom_lfd',
    filterParams: dateServerSideFilterParams,
    cellDataType: 'dateString',
    editable: false,
    isDate: true,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Yard LFD',
    field: 'yard_lfd',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'yard_last_free_date',
    filterParams: dateServerSideFilterParams,
    isDate: true,
    floatingFilter: true,
  },
  {
    headerName: 'Date Created',
    field: 'created_at',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: {
      ...dateServerSideFilterParams,
    },
    floatingFilter: true,
    sortingOrder: ['desc', 'asc'],
  },
  {
    headerName: 'Stop Arrived',
    field: 'arrived_date',
    filter: 'agDateColumnFilter',
    cellDataType: 'dateString',
    isDate: true,
    editable: false,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'ETA',
    field: 'eta',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    schema_value: 'estimated_arrival',
    isDate: true,
    editable: false,
    filterParams: dateServerSideFilterParams,
    updateFunc: onDateCellEdit,
    floatingFilter: true,
  },
  {
    headerName: 'Missing Docs Report',
    field: 'missing_docs_report_sent',
    hide: true,
    cellRenderer: statusFormatter,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Shipper Total',
    field: 'shipperCost',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params && params.data && params?.data?.shipperCost >= 0) {
        return formatMoney(
          params?.data?.shipperCost,
          params?.data?.shipment_currency,
        );
      }
      return '';
    },
    cellRenderer: (param) => {
      return param.valueFormatted || '';
    },
  },
  {
    headerName: 'Carrier Total',
    field: 'carrierCost',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params && params.data && params?.data?.carrierCost >= 0) {
        return formatMoney(
          params?.data?.carrierCost,
          params?.data?.shipment_currency,
        );
      }
      return '';
    },
    cellRenderer: (param) => {
      return param.valueFormatted || '';
    },
  },
  {
    headerName: 'Vendor Total',
    field: 'vendorCost',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params && params.data && params?.data?.vendorCost >= 0) {
        return formatMoney(
          params?.data?.vendorCost,
          params?.data?.shipment_currency,
        );
      }
      return '';
    },
    cellRenderer: (param) => {
      return param.valueFormatted || '';
    },
  },
  {
    headerName: 'Base Rate',
    field: 'base_rate',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
  },
  {
    headerName: 'Shipper Chassis Total',
    headerTooltip: 'SHIPPER_CHASSIS_TOTAL_TOOLTIP',
    titleize: true,
    field: 'chassis',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (param) =>
      currencyFormatter(param.data.chassis, {
        currency: param.data.shipment_currency,
      }),
    cellRenderer: moneyValueRenderer,
  },
  {
    headerName: 'Shipper Demurrage total',
    headerTooltip: 'SHIPPER_DEMURRAGE_TOTAL_TOOLTIP',
    titleize: true,
    field: 'demurrage',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (param) =>
      currencyFormatter(param.data.demurrage, {
        currency: param.data.shipment_currency,
      }),
    cellRenderer: moneyValueRenderer,
  },
  {
    headerName: 'Shipper Storage Total',
    headerTooltip: 'SHIPPER_STORAGE_TOTAL_TOOLTIP',
    titleize: true,
    field: 'storage',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (param) =>
      currencyFormatter(param.data.storage, {
        currency: param.data.shipment_currency,
      }),
    cellRenderer: moneyValueRenderer,
  },
  {
    headerName: 'Other',
    titleize: true,
    field: 'other',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
  },
  {
    headerName: 'Total',
    titleize: true,
    field: 'total',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
  },
  {
    headerName: 'On Hold',
    field: 'on_hold_reason',
    cellRenderer: lineSeparatedStringFormatter,
    wrapText: true,
    autoHeight: true,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'POD Valid',
    field: 'is_pod_valid',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Has Rail Events',
    field: 'has_rail_events',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Days Since Delivery',
    field: 'days_since_loaded_delivery',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
  },
  {
    headerName: 'Days Since Delivery Bin',
    field: 'days_since_loaded_delivery_bin',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      values: [
        'Returned',
        '< Week',
        '< 2 Weeks',
        '< 1 Month',
        '< 2 Months',
        '< 3 Months',
        '3+ Months',
        null,
      ],
      // turn off standard sorting of values
      comparator: () => 1,
    },
  },
  {
    headerName: 'LFD Bin',
    field: 'last_free_date_bin',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      values: [
        'No LFD',
        'Week+ Before',
        '6+ Days Before LFD',
        '6 Days Before LFD',
        '5 Days Before LFD',
        '4 Days Before LFD',
        '3 Days Before LFD',
        '2 Days Before LFD',
        '1 Day Before LFD',
        'LFD Today',
        '1 Day After LFD',
        '2 Days After LFD',
        '3 Days After LFD',
        '4 Days After LFD',
        '5 Days After LFD',
        '6 Days After LFD',
        '6+ Days After LFD',
      ],
      // turn off standard sorting of values
      comparator: () => 1,
    },
  },
  {
    headerName: 'Combo Outgated Date',
    field: 'combo_outgated_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Combo LFD Date',
    field: 'combo_last_free_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Outgated',
    field: 'outgated',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Empty Returned',
    field: 'empty_returned',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Loaded on Rail',
    field: 'rail_loaded',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Departed on Rail',
    field: 'rail_departed',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Arrived at Rail Facility',
    field: 'rail_arrived_at_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Unloaded From Rail',
    field: 'rail_unloaded',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Available at Rail Facility',
    field: 'rail_available_at_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Full Outgated From Rail Facility',
    field: 'rail_outgated_from_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'LFD at Rail Facility',
    field: 'rail_lfd_at_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'ETA to Final Rail Facility',
    field: 'rail_eta_final_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Full Ingated at Rail Facility',
    field: 'rail_ingated_at_origin_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Loaded on Rail at Origin',
    field: 'rail_loaded_at_origin',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Departed on Rail From Origin',
    field: 'rail_departed_from_origin',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Arrived At Destination Port Rail Facility',
    field: 'rail_arrived_at_destination_port_facility',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Unloaded From Rail at Destination Port',
    field: 'rail_unloaded_at_destination_port',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Empty Rail Billing Date',
    field: 'empty_rail_billing',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Loaded Rail Billing Date',
    field: 'loaded_rail_billing',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Must Arrive by Date',
    field: 'must_arrive_by_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Requested Pickup Date',
    field: 'requested_pickup_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Requested Delivery Appointment',
    field: 'requested_delivery_appointment',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Planned Delivery Date',
    field: 'shipment_planned_delivery_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Pending Shipper Accessorials',
    field: 'pending_shipper_accessorials',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Pending Carrier Accessorials',
    field: 'pending_carrier_accessorials',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Per Diem LFD',
    field: 'per_diem_lfd',
    cellRenderer: dateFormatter,
    filter: 'agDateColumnFilter',
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Automated Billing Evaluation Paused',
    field: 'shipment_automated_billing_status_evaluation_paused',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  {
    headerName: 'Automated Billing Evaluation Paused Reason',
    field: 'shipment_automated_billing_status_evaluation_reason_code',
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Automated Billing Evaluated Paused Timestamp',
    field: 'shipment_automated_billing_status_evaluation_paused_timestamp',
    cellRenderer: dateFormatter,
    filter: 'agDateColumnFilter',
    isDate: true,
    filterParams: dateServerSideFilterParams,
    floatingFilter: true,
  },
  {
    headerName: 'Discussion',
    field: 'comments',
    autoHeight: true,
    editable: true,
    cellEditor: CommentsEditor,
    cellEditorParams: {
      onCommentAdd: onShipmentCommentAdd,
    },
    cellEditorPopup: true,
    filter: false,
    valueFormatter: (params) => {
      const count = params?.data?.comments?.length || 0;
      if (!count) {
        return '';
      }
      const postfix = count === 1 ? 'comment' : 'comments';
      return `${count} ${postfix}`;
    },
  },
  {
    headerName: 'Document Cutoff Date',
    field: 'document_cutoff_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Rail Earliest Return',
    field: 'rail_earliest_return',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Rail Cut Date',
    field: 'rail_cut_date',
    filter: 'agDateColumnFilter',
    valueFormatter: dateFormatter,
    filterParams: dateServerSideFilterParams,
  },
  {
    headerName: 'Total Invoiced',
    field: 'total_invoiced',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'TOTAL_INVOICED_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.total_invoiced >= 0) {
        return formatMoney(
          params?.data?.total_invoiced,
          params?.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Shipper Cost Total',
    field: 'shipper_cost_total',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.shipper_cost_total) {
        return formatMoney(
          params.data.shipper_cost_total,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Carrier Cost Total',
    field: 'carrier_cost_total',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.carrier_cost_total) {
        return formatMoney(
          params.data.carrier_cost_total,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Shipper Accessorial Total',
    field: 'shipper_accessorial_total',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'SHIPPER_ACCESSORIAL_TOTAL_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.shipper_accessorial_total) {
        return formatMoney(
          params.data.shipper_accessorial_total,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Carrier Accessorial Total',
    field: 'carrier_accessorial_total',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    headerTooltip: 'CARRIER_ACCESSORIAL_TOTAL_TOOLTIP',
    valueFormatter: (params) => {
      if (params?.data?.carrier_accessorial_total) {
        return formatMoney(
          params.data.carrier_accessorial_total,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Shipper Other Accessorial Total',
    field: 'shipper_other_accessorial_total',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'SHIPPER_OTHER_ACCESSORIAL_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.shipper_other_accessorial_total) {
        return formatMoney(
          params.data.shipper_other_accessorial_total,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Carrier Other Accessorial Total',
    field: 'carrier_other_accessorial_total',
    ...getNumberFilter(),
    cellDataType: 'number',
    type: 'rightAligned',
    headerTooltip: 'CARRIER_OTHER_ACCESSORIAL_TOOLTIP',
    valueFormatter: (params) => {
      if (params?.data?.carrier_other_accessorial_total) {
        return formatMoney(
          params.data.carrier_other_accessorial_total,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },

  {
    headerName: 'Unbilled Approved',
    field: 'unbilled_approved',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'UNBILLED_APPROVED_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.unbilled_approved) {
        return formatMoney(
          params?.data?.unbilled_approved,
          params?.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Unbilled Pending',
    field: 'unbilled_pending',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'UNBILLED_PENDING_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.unbilled_pending) {
        return formatMoney(
          params.data.unbilled_pending,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Carrier Fsc Total',
    field: 'carrier_fsc_total',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'CARRIER_FSC_TOTAL_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.carrier_fsc_total) {
        return formatMoney(
          params.data.carrier_fsc_total,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Shipper Fsc Total',
    field: 'shipper_fsc_total',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'SHIPPER_FSC_TOTAL_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.shipper_fsc_total) {
        return formatMoney(
          params.data.shipper_fsc_total,
          params.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Shipper Linehaul',
    field: 'shipper_linehaul',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'SHIPPER_LINEHAUL_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.shipper_linehaul) {
        return formatMoney(
          params?.data?.shipper_linehaul,
          params?.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Carrier Linehaul',
    field: 'carrier_linehaul',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'CARRIER_LINEHAUL_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (params?.data?.carrier_linehaul) {
        return formatMoney(
          params?.data?.carrier_linehaul,
          params?.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Carrier Payout Due',
    field: 'carrier_unpaid_total',
    ...getNumberFilter(),
    cellDataType: 'number',
    headerTooltip: 'CARRIER_PAYOUT_DUE_TOOLTIP',
    type: 'rightAligned',
    valueFormatter: (params) => {
      if (
        params?.data?.carrier_unpaid_total != null &&
        params?.data?.carrier_unpaid_total >= 0
      ) {
        return formatMoney(
          params?.data?.carrier_unpaid_total,
          params?.data?.shipment_currency,
        );
      }
      return '';
    },
  },
  {
    headerName: 'Team',
    groupId: 'team',
    hide: false,
    marryChildren: false,
    children: [
      {
        headerName: 'Commercial',
        groupId: 'commercial',
        hide: false,
        marryChildren: false,
        children: [
          {
            headerName: 'Sales Rep',
            field: 'commercial_sales_rep',
            filterParams: {
              ...textDefaultFilterParams,
            },
          },
          {
            headerName: 'Account Manager',
            field: 'commercial_account_manager',
            filterParams: {
              ...textDefaultFilterParams,
            },
          },
        ],
      },
      ...(view === 'drayage'
        ? [
            {
              headerName: 'Drayage Ops',
              groupId: 'drayage_ops',
              hide: false,
              marryChildren: false,
              children: [
                {
                  headerName: 'Data Entry',
                  field: 'drayage_ops_data_entry',
                  filterParams: {
                    ...textDefaultFilterParams,
                  },
                },
                {
                  headerName: 'Customer Success',
                  field: 'drayage_ops_customer_success',
                  filterParams: {
                    ...textDefaultFilterParams,
                  },
                },
              ],
            },
          ]
        : []),
      ...(view === 'shorthaul'
        ? [
            {
              headerName: 'Shorthaul Ops',
              groupId: 'shorthaul_ops',
              hide: false,
              marryChildren: false,
              children: [
                {
                  headerName: 'Operations Manager',
                  field: 'shorthaul_ops_operations_manager',
                  filterParams: {
                    ...textDefaultFilterParams,
                  },
                },
                {
                  headerName: 'Customer Success',
                  field: 'shorthaul_ops_customer_success',
                  filterParams: {
                    ...textDefaultFilterParams,
                  },
                },
              ],
            },
          ]
        : []),
    ],
  },
  {
    headerName: 'Exposed',
    field: 'exposed',
    cellRenderer: checkedFormatter,
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
  },
  ...(includeAmazonPreArrival
    ? [
        {
          headerName: 'Pre-Notice / Final',
          field: 'shipment_pre_arrival_status',
          cellRenderer: ({ value }) => {
            return (
              <Badge color={SHIPMENT_PRE_ARRIVAL_STATUS_BADGE_COLOR[value]}>
                {titleize(humanize(t(value)))}
              </Badge>
            );
          },
          filter: 'agSetColumnFilter',
          filterParams: {
            buttons: ['reset', 'apply'],
            valueFormatter: ({ value }) => {
              return titleize(humanize(t(value)));
            },
            values: SHIPMENT_PRE_ARRIVAL_STATUS_FILTER_LIST,
          },
          autoHeight: true,
        },
      ]
    : []),
];

export const getColumnDefs = (view, t) => {
  const columns =
    view === 'drayage' ? getDrayageColumns() : getShorthaulColumns();
  return columns
    .map((incoming) => {
      const column = { ...incoming };
      column.menuTabs = ['filterMenuTab', 'generalMenuTab'];
      if (column.field === 'created_at') {
        column.filterParams.minValidDate = getMinValidDate();
      }
      if (
        isUS() &&
        column.field &&
        column.field.toLowerCase().includes('state')
      ) {
        column.filter = 'agSetColumnFilter';
        column.floatingFilter = true;
        column.filterParams = {
          values: usStates.map((state) => state.abbreviation),
        };
      }

      return column;
    })
    .map((c) => {
      c.headerName = c.titleize
        ? firstLetterTitlization(t(sluggify(c.headerName)))
        : t(sluggify(c.headerName));
      return c;
    });
};

export { getBaseColumnDefs };

export const getDrayageColumns = () => {
  const drayageWhitelist = [
    'order_carrier_name',
    'pickup_stop',
    'delivery_stop',
    'empty_delivery_stop',
    'pod_status',
    'mode',
    'move_type',
    'tags',
    'admin_notes',
    'notes',
    'cargo_description',
    'reference_numbers',
    'hazmat_description',
    'marketCode',
    'shipment_origin',
    'shipment_origin_name',
    'shipment_origin_city',
    'shipment_origin_state',
    'shipment_destination',
    'shipment_destination_name',
    'shipment_destination_city',
    'shipment_destination_state',
    'hazmat_class',
    'originating',
    'service_type',
    'load_details',
    'hazmat_packaging_group',
    'steamship_or_railroad',
    'railroads',
    'pre_notice_accessorials',
    'review_date',
    'invoice_cliff_date',
    'original_dispatch_date',
    'current_dispatch_date',
    'pickup_from_exam_site',
    'dropoff_at_exam_site',
    'accounting_review_date',
    'arrival_date',
    'discharge_date',
    'available_date',
    'outgated_date',
    'empty_ready_for_pickup',
    'empty_returned_date',
    'last_cut_date',
    'earliest_return_date',
    'earliest_empty_date',
    'total_weight',
    'shipment_weight',
    'distance',
    'leg_number',
    'leg_type',
    'hub_type',
    'has_required_docs',
    'shipment_id',
    'shipmentStatus',
    'order_id',
    'orderStatus',
    'shipment_created_by',
    'equipment_id',
    'chassis_number',
    'trailer_number',
    'order_type',
    'margin',
    'marketCode',
    'opentrack_container_status',
    'opentrack_container_status_2',
    'opentrack_tracking_status',
    'master_bol_status',
    'container_status',
    'delivery_order_status',
    'pickup_number',
    'so_number',
    'shipper_name',
    'terminal_hold_status',
    'shipper_operations_group',
    'shipper_operations_manager',
    'bco',
    'order_carrier_name',
    'carrier_name',
    'driver_name',
    'vessel_or_rail',
    'train',
    'lfd',
    'custom_lfd',
    'yard_lfd',
    'created_at',
    'arrived_date',
    'eta',
    'missing_docs_report_sent',
    'shipperCost',
    'carrierCost',
    'vendorCost',
    'base_rate',
    'chassis',
    'demurrage',
    'storage',
    'other',
    'total',
    'on_hold_reason',
    'is_pod_valid',
    'has_rail_events',
    'days_since_loaded_delivery',
    'days_since_loaded_delivery_bin',
    'last_free_date_bin',
    'combo_outgated_date',
    'combo_last_free_date',
    'outgated',
    'empty_returned',
    'rail_loaded',
    'rail_departed',
    'rail_arrived_at_facility',
    'rail_unloaded',
    'rail_available_at_facility',
    'rail_outgated_from_facility',
    'rail_lfd_at_facility',
    'rail_eta_final_facility',
    'rail_ingated_at_origin_facility',
    'rail_loaded_at_origin',
    'rail_departed_from_origin',
    'rail_arrived_at_destination_port_facility',
    'rail_unloaded_at_destination_port',
    'empty_rail_billing',
    'loaded_rail_billing',
    'must_arrive_by_date',
    'requested_pickup_date',
    'requested_delivery_appointment',
    'shipment_planned_delivery_date',
    'pending_shipper_accessorials',
    'pending_carrier_accessorials',
    'per_diem_lfd',
    'shipment_automated_billing_status_evaluation_paused',
    'shipment_automated_billing_status_evaluation_reason_code',
    'shipment_automated_billing_status_evaluation_paused_timestamp',
    'comments',
    'document_cutoff_date',
    'rail_earliest_return',
    'rail_cut_date',
    'total_invoiced',
    'shipper_cost_total',
    'carrier_cost_total',
    'shipper_accessorial_total',
    'carrier_accessorial_total',
    'shipper_other_accessorial_total',
    'carrier_other_accessorial_total',
    'unbilled_approved',
    'unbilled_pending',
    'carrier_fsc_total',
    'shipper_fsc_total',
    'shipper_linehaul',
    'carrier_linehaul',
    'carrier_unpaid_total',
    'team',
    'exposed',
    'billing_status_state_has_required_accessorials',
    'billing_status_state_missing_and_pending_required_accessorial_types',
    'billing_status_state_has_required_billable_stops',
    'billing_status_state_pending_billable_stops',
    'shipment_pre_arrival_status',
    'shipper_subsidiary',
    'carrier_subsidiary',
    'carrier_driver_type',
    'chassis_in_gate',
    'chassis_out_gate',
  ];

  const shouldShow = (field) =>
    DEFAULT_DRAYAGE_COLS_OPS.some((pattern) => {
      if (pattern.startsWith('startsWith:')) {
        const startsWidth = pattern.slice(11);
        return field.startsWith(startsWidth);
      } else {
        return pattern === field;
      }
    });

  function setHideFlags(columns) {
    if (!columns) {
      return;
    }
    if (!Array.isArray(columns)) {
      setHideFlags([columns]);
      return;
    }
    columns.forEach((col) => {
      col.hide = false;

      if (col.field) {
        col.hide = !shouldShow(col.field);
      }
      // if it is still visible, keep going down
      if (!col.hide && col.children) {
        setHideFlags(col.children);
      }
    });
  }

  // Filter base columns first, then append group columns (unfiltered)
  const filteredBase = getBaseColumnDefs(
    'drayage',
    featureEnabled(CONFIGURED_FEATURES.AMAZON_PRE_ARRIVAL),
  ).filter((c) => drayageWhitelist.includes(c.field || c.groupId));

  const combined = getBillingStatusStateGroup()
    .concat(...getReferenceNumberGroup('drayage'))
    .concat(...filteredBase)
    .concat(...getAdditionalStopGroupsColumnDefs())
    .concat(...getDrayageStopGroupsColumnDefs(), {
      ...getDrayageShipmentTypes(),
    })
    .concat(...getBridgeReasonCodeGroup())
    .map((c) => {
      const col = { ...c };
      setHideFlags(col);

      if (col.pinnedFor && col.pinnedFor.indexOf('drayage') > -1) {
        col.pinned = true;
      }
      return col;
    });

  return _sortBy(combined, 'priority');
};

export const getShorthaulColumns = () => {
  const shorthaulWhitelist = [
    'shipment_id',
    'shipmentStatus',
    'closed',
    'canceled',
    'admin_notes',
    'notes',
    'shipment_created_by',
    'shipper_name',
    'order_carrier_name',
    'carrier_name',
    'driver_name',
    'driver_phone_number',
    'tags',
    'mode',
    'move_type',
    'cargo_description',
    'reference_numbers',
    'hazmat_description',
    'shipment_origin',
    'shipment_origin_name',
    'shipment_origin_city',
    'shipment_origin_state',
    'shipment_destination',
    'shipment_destination_name',
    'shipment_destination_city',
    'shipment_destination_state',
    'shipment_type',
    'hazmat_class',
    'originating',
    'load_details',
    'hazmat_packaging_group',
    'steamship_or_railroad',
    'railroads',
    'review_date',
    'invoice_cliff_date',
    'original_dispatch_date',
    'current_dispatch_date',
    'pickup_from_exam_site',
    'dropoff_at_exam_site',
    'accounting_review_date',
    'arrival_date',
    'discharge_date',
    'available_date',
    'outgated_date',
    'empty_ready_for_pickup',
    'empty_returned_date',
    'last_cut_date',
    'earliest_return_date',
    'earliest_empty_date',
    'total_weight',
    'shipment_weight',
    'distance',
    'has_required_docs',
    'order_id',
    'orderStatus',
    'equipment_id',
    'chassis_number',
    'trailer_number',
    'order_type',
    'margin',
    'marketCode',
    'opentrack_container_status',
    'opentrack_container_status_2',
    'opentrack_tracking_status',
    'master_bol_status',
    'container_status',
    'delivery_order_status',
    'pickup_number',
    'so_number',
    'terminal_hold_status',
    'shipper_operations_group',
    'shipper_operations_manager',
    'bco',
    'vessel_or_rail',
    'train',
    'lfd',
    'custom_lfd',
    'yard_lfd',
    'created_at',
    'arrived_date',
    'eta',
    'missing_docs_report_sent',
    'shipperCost',
    'carrierCost',
    'vendorCost',
    'base_rate',
    'chassis',
    'demurrage',
    'storage',
    'other',
    'total',
    'on_hold_reason',
    'is_pod_valid',
    'has_rail_events',
    'days_since_loaded_delivery',
    'days_since_loaded_delivery_bin',
    'last_free_date_bin',
    'combo_outgated_date',
    'combo_last_free_date',
    'outgated',
    'empty_returned',
    'rail_loaded',
    'rail_departed',
    'rail_arrived_at_facility',
    'rail_unloaded',
    'rail_available_at_facility',
    'rail_outgated_from_facility',
    'rail_lfd_at_facility',
    'rail_eta_final_facility',
    'rail_ingated_at_origin_facility',
    'rail_loaded_at_origin',
    'rail_departed_from_origin',
    'rail_arrived_at_destination_port_facility',
    'rail_unloaded_at_destination_port',
    'empty_rail_billing',
    'loaded_rail_billing',
    'must_arrive_by_date',
    'requested_pickup_date',
    'requested_delivery_appointment',
    'shipment_planned_delivery_date',
    'pending_shipper_accessorials',
    'pending_carrier_accessorials',
    'per_diem_lfd',
    'shipment_automated_billing_status_evaluation_paused',
    'shipment_automated_billing_status_evaluation_reason_code',
    'shipment_automated_billing_status_evaluation_paused_timestamp',
    'comments',
    'document_cutoff_date',
    'rail_earliest_return',
    'rail_cut_date',
    'total_invoiced',
    'shipper_cost_total',
    'carrier_cost_total',
    'shipper_accessorial_total',
    'carrier_accessorial_total',
    'shipper_other_accessorial_total',
    'carrier_other_accessorial_total',
    'unbilled_approved',
    'unbilled_pending',
    'carrier_fsc_total',
    'shipper_fsc_total',
    'shipper_linehaul',
    'carrier_linehaul',
    'team',
    'exposed',
    'shipper_accessorial_total',
    'carrier_accessorial_total',
    'original_dispatch_date',
    'current_dispatch_date',
  ];

  // Filter base columns first, then append group columns (unfiltered)
  const filteredBase = getBaseColumnDefs(
    'shorthaul',
    featureEnabled(CONFIGURED_FEATURES.AMAZON_PRE_ARRIVAL),
  ).filter((c) => shorthaulWhitelist.includes(c.field || c.groupId));

  const combined = filteredBase
    .concat(...getReferenceNumberGroup('shorthaul'))
    .concat(...getAdditionalStopGroupsColumnDefs())
    .concat(...getShorthaulStopGroupsColumnDefs())
    .map((c) => {
      const col = { ...c };
      if (col.pinnedFor && col.pinnedFor.indexOf('shorthaul') > -1) {
        col.pinned = true;
      }
      return col;
    });

  return _sortBy(combined, 'priority');
};

export const allColumns = getBaseColumnDefs(
  'default',
  featureEnabled(CONFIGURED_FEATURES.AMAZON_PRE_ARRIVAL),
);

export const allFilterOptions = [
  ...textDefaultFilterParams.filterOptions,
  ...dateServerSideFilterParams.filterOptions,
];

export const getShipperBlacklist = async () => {
  const blacklistedColumns = await getColumnBlacklist();
  return { blacklistedColumns };
};

export const getShipperDrayageColumns = () => {
  const adminColumns = getDrayageColumns();
  return adminColumns.map((col) => {
    if (col.field === 'shipment_id') {
      col.cellRenderer = shipperShipmentIDRenderer;
    }
    return col;
  });
};

export const getShipperShorthaulColumns = async (t) => {
  const columnDefs = getColumnDefs('shorthaul', t);
  const blacklistedColumns = await getColumnBlacklist();
  const filteredColumns = columnDefs.filter(
    (col) => !blacklistedColumns[col.field],
  );
  return { filteredColumns, blacklistedColumns };
};

export const filterOptionSlugs = allFilterOptions.reduce(
  (acc, filterOption) => {
    if (typeof filterOption === 'string') {
      acc[filterOption] = sluggify(filterOption);
    } else {
      acc[filterOption.displayKey] = sluggify(filterOption.displayName);
    }

    return acc;
  },
  {},
);
